import { Button, buttonVariants } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth-store';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Suspense, useEffect, useState } from 'react';
import Loader from '@/components/ui/loader';
import { toast } from 'sonner';

const AUTH_SIGN_IN_URL = import.meta.env.VITE_AUTH_SIGN_IN_URL;

function safeReturnPath(returnUrl: string | null): string | null {
  if (!returnUrl || !returnUrl.startsWith('/') || returnUrl.includes('//')) return null;
  return returnUrl;
}

function SignInContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customToken = searchParams.get('customToken') ?? searchParams.get('token');
  const returnUrlParam = searchParams.get('returnUrl');
  const displayName = searchParams.get('displayName') ?? undefined;
  const profileImageUrl = searchParams.get('profileImageUrl') ?? undefined;

  const authState = useAuthStore((s) => s.authState);
  const initAuth = useAuthStore((s) => s.init);
  const signInWithToken = useAuthStore((s) => s.signInWithToken);

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const safeReturnUrl = safeReturnPath(returnUrlParam);
  const defaultRedirect = safeReturnUrl ?? '/';

  const goToSignIn = () => {
    if (!AUTH_SIGN_IN_URL) return;
    const backTo = `${window.location.origin}/sign-in${
      safeReturnUrl ? `?returnUrl=${encodeURIComponent(safeReturnUrl)}` : ''
    }`;
    const url = `${AUTH_SIGN_IN_URL}${AUTH_SIGN_IN_URL.includes('?') ? '&' : '?'}returnUrl=${encodeURIComponent(
      backTo
    )}`;
    window.location.href = url;
  };

  useEffect(() => {
    initAuth();
  }, []);

  useEffect(() => {
    if (customToken) return;
    if (authState === 'signed-in') navigate(defaultRedirect, { replace: true });
  }, [authState, customToken, defaultRedirect]);

  useEffect(() => {
    if (!customToken || status !== 'idle') return;
    const t = setTimeout(async () => {
      setStatus('loading');
      try {
        await signInWithToken({ token: customToken, displayName, profileImageUrl });
        setStatus('success');
        navigate(defaultRedirect, { replace: true });
      } catch (err) {
        setStatus('error');
        toast.error(err instanceof Error ? err.message : 'Sign-in failed.');
      }
    }, 0);
    return () => clearTimeout(t);
  }, [customToken, defaultRedirect, displayName, profileImageUrl, status]);

  return (
    <div className="bg-background flex min-h-screen flex-col items-center justify-center px-4">
      {status === 'loading' && <p className="text-muted-foreground">Signing you in…</p>}
      {status === 'success' && <p className="text-muted-foreground">Redirecting…</p>}
      {status === 'error' && (
        <div className="space-y-4">
          {AUTH_SIGN_IN_URL && (
            <Button variant="default" onClick={goToSignIn}>
              Try signing in again
            </Button>
          )}
        </div>
      )}
      {status === 'idle' && !customToken && !AUTH_SIGN_IN_URL && (
        <div className="space-y-4">
          <h1 className="text-foreground text-xl font-semibold">Sign in</h1>
          <p className="text-muted-foreground text-sm">
            Configure VITE_AUTH_SIGN_IN_URL or use the link from your sign-in email.
          </p>
          <Link to="/" className={cn(buttonVariants({ variant: 'outline' }))}>
            Back to dashboard
          </Link>
        </div>
      )}
      {status === 'idle' && !customToken && AUTH_SIGN_IN_URL && (
        <div className="border p-8 text-center">
          <h1 className="text-foreground text-xl font-semibold">Sign in</h1>
          <p className="text-muted-foreground text-sm">
            Continue to sign in; you'll be sent back here with your session.
          </p>
          <Button className={'mt-4'} variant="default" onClick={goToSignIn}>
            Continue to sign in
          </Button>
        </div>
      )}
    </div>
  );
}

export function SignInPage() {
  return (
    <Suspense
      fallback={
        <div className="bg-background flex min-h-screen flex-col items-center justify-center gap-4 p-8">
          <Loader />
        </div>
      }
    >
      <SignInContent />
    </Suspense>
  );
}
